import { z } from "zod";
import { Estimate, LineInput, SheetInput } from "./estimate.js";
import { OrderLinkInfo } from "./order.js";
import { UNISSUED_MSG } from "./unissued.js";

// ---------------------------------------------------------------------------
// 支払通知書（仕入明細書）と確認ページ（注文書の3回目 3b、#233、docs/93 §15.6・§15.12）。DB 20261026_1500
//   請求書を出さない{vendor}に、元請が作って渡す仕入明細書（documents の doc_type payment_notice）。名前は会社の言葉 {paymentNotice}
//   （支払通知書⇄支払明細書。設定 → {order}）。両製品・門はモジュール sales。
//   作る入口：承諾済の{order}の「⋯」→「{paymentNotice}作成」（行と条件を写す。金額は直せる）／左のメニュー「仕入」の一覧の
//   「{paymentNotice}作成」（{order}なし・{vendor}と{project}）。作成画面は{order}と同じ骨組み（社内用の列なし）。
//   相手の確認：確認ページ /n/<鍵>（ログインなし・承諾ページ /o/ と同じ土台）で「確認」1回 か「誤りがある」（理由 200字）。
//   送付後 n日（設定 → {order} の「確認の期限」。既定 7日・3〜30）を過ぎて連絡が無ければ「確認済（期限経過）」（毎日の掃除の仕事）。
//   確認済（画面でも期限経過でも）で仕入を起こして確定する（税額は通知書のとおり・原本は通知書の PDF）。誤りがあると連絡があれば起こさない
//   （元請が取消して出し直す）。取消は確認の前だけ（確認のあとの直しは仕入の側。3b では訂正版なし）。
// ---------------------------------------------------------------------------

export const PAYMENT_NOTICE_LIMITS = {
  /** 確認の期限（送付後 n日）の既定・下限・上限（DB の fn_order_settings_ok と同じ） */
  confirmDaysDefault: 7,
  confirmDaysMin: 3,
  confirmDaysMax: 30,
  /** 確認ページの鍵の期限＝確認の期限＋この日数（送付の前は 発行の日＋確認の期限＋この日数） */
  linkExtraDays: 30,
  /** 「誤りがある」の理由 */
  reasonMax: 200,
} as const;

/** 確認ページの鍵（URL の /n/<鍵>）。32 バイトの乱数の base64url（43 字）。承諾ページと同じ形 */
export const NOTICE_TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;

/**
 * 確認の一文（紙の消せない部品「確認の一文」＝国税庁 インボイス Q&A 問86 の3つ目の方法）。
 * 名前が「…通知書」なら「本通知書」、「…明細書」なら「本明細書」、ほかは「本書」
 */
export function noticeConfirmText(days: number, name = "支払通知書"): string {
  const self = name.endsWith("通知書") ? "本通知書" : name.endsWith("明細書") ? "本明細書" : "本書";
  return `${self}の内容に誤りがある場合は、送付後 ${days}日以内にご連絡ください。ご連絡がない場合は確認いただいたものとします。`;
}

/** 回の条件（document_revisions.notice_terms）。下書きは元の{order}だけ・出す瞬間に固まる */
export const NoticeTerms = z.object({
  v: z.literal(1).default(1),
  orderId: z.string().uuid().nullable().default(null),
  confirmDays: z.number().int().min(PAYMENT_NOTICE_LIMITS.confirmDaysMin).max(PAYMENT_NOTICE_LIMITS.confirmDaysMax).nullable().default(null),
  vendorRegNo: z.string().max(14).default(""),
  vendorQualified: z.boolean().nullable().default(null),
  creditRatio: z.number().min(0).max(1).nullable().default(null),
});
export type NoticeTerms = z.infer<typeof NoticeTerms>;

/**
 * 状態（一覧と見出しのチップ）：draft 下書き／unissued 下書き（取り消して下書きに戻した＝番号あり。#278）／unsent 未送付／
 * waiting 確認待ち n日（送付から）／confirmed 確認済 MM/DD／expired 確認済（期限経過）／disputed 誤りあり
 */
export const NoticeState = z.enum(["draft", "unissued", "unsent", "waiting", "confirmed", "expired", "disputed"]);
export type NoticeState = z.infer<typeof NoticeState>;

/** 一覧のタブ（仕入の一覧の「{paymentNotice}」の中） */
export const NoticeTab = z.enum(["waiting", "confirmed", "all"]);
export type NoticeTab = z.infer<typeof NoticeTab>;
export const NOTICE_TAB_LABEL: Record<NoticeTab, string> = { waiting: "確認待ち", confirmed: "確認済", all: "すべて" };
/** 確認待ちのタブに入る状態（下書き（番号ありも）・未送付・誤りありも＝相手の確認で終わっていないもの） */
export const isNoticeWaitingTab = (s: NoticeState): boolean => s !== "confirmed" && s !== "expired";

/** 相手（{vendor}）の登録番号と、免税事業者の控除割合（下書き＝今の見込み・出したあと＝固めた値） */
export const NoticeVendorTax = z.object({
  registrationNo: z.string(),
  qualified: z.boolean(),
  /** 控除割合（0.7 の形）。適格請求書発行事業者なら null */
  creditRatio: z.number().nullable(),
  /** 控除割合の期間の終わり（「70%（経過措置 ～2028/9）」） */
  creditUntil: z.string().nullable(),
});
export type NoticeVendorTax = z.infer<typeof NoticeVendorTax>;

/** 支払通知書：見積の形（行・内訳・計算の見え方は見積のエディタ）に、支払通知書だけの欄を足したもの */
export const PaymentNotice = Estimate.extend({
  revision: Estimate.shape.revision.extend({ notice: NoticeTerms }),
  /** 帳票の名前（下書き＝今の会社の言葉・出したあと＝固めた名前）。order は元の{order}の名前（会社の言葉） */
  names: z.object({ paymentNotice: z.string(), order: z.string() }),
  vendorTax: NoticeVendorTax,
  /** 確認の期限（送付後 n日。下書き＝会社の設定・出したあと＝固めた値） */
  confirmDays: z.number().int(),
  state: NoticeState,
  /** いちばん早い送付の日時（送付の記録）。送っていなければ null */
  sentAt: z.string().nullable(),
  /** 確認の期限の日（送付の日＋n日）。送っていなければ null */
  deadline: z.string().nullable(),
  /** 確認待ちの日数（送付から） */
  waitingDays: z.number().int().nullable(),
  confirm: z.object({ method: z.enum(["screen", "expired"]), at: z.string(), recordNo: z.string(), email: z.string().nullable() }).nullable(),
  dispute: z.object({ at: z.string(), reason: z.string() }).nullable(),
  link: OrderLinkInfo.nullable(),
  /** 元の{order}（作成のときに写した。Estimate の order＝受注の状態とは別の名前） */
  sourceOrder: z.object({ id: z.string().uuid(), displayNo: z.string().nullable(), subject: z.string() }).nullable(),
  /** 確認済で起こした仕入 */
  purchase: z.object({ id: z.string().uuid(), status: z.enum(["draft", "confirmed", "voided"]) }).nullable(),
  /** 取消しの断り（確認済） */
  unissueBlockedBy: z.enum(["confirmed"]).nullable(),
});
export type PaymentNotice = z.infer<typeof PaymentNotice>;

/** POST /payment-notices：承諾済の{order}から（行と条件を写す）・{vendor}と{project}（{order}なし） */
export const PaymentNoticeCreate = z
  .object({
    fromOrderId: z.string().uuid().optional(),
    vendorId: z.string().uuid().optional(),
    projectGroupId: z.string().uuid().nullable().optional(),
    subject: z.string().trim().max(200).optional(),
  })
  .refine((v) => !!v.fromOrderId || !!v.vendorId, { message: "{vendor}を選んでください", path: ["vendorId"] });
export type PaymentNoticeCreate = z.infer<typeof PaymentNoticeCreate>;

/** PUT /payment-notices/:id（版つき。違えば 409 PAYMENT_NOTICE_STALE）。省いた欄は変えない。下書きだけ */
export const PaymentNoticeSave = z.object({
  subject: z.string().max(200).optional(),
  issueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  notes: z.string().max(2000).optional(),
  /** {vendor}を選び直す（下書きだけ。外注先・両方だけ） */
  partnerId: z.string().uuid().optional(),
  /** {project}の付け替え（null で外す） */
  projectGroupId: z.string().uuid().nullable().optional(),
  sheets: z.array(SheetInput).max(50).optional(),
  lines: z.array(LineInput).max(1000).optional(),
  version: z.string().min(1).max(40).optional(),
});
export type PaymentNoticeSave = z.infer<typeof PaymentNoticeSave>;

export const PaymentNoticeIssueRequest = z.object({
  company: z.object({ name: z.string().trim().min(1).max(100) }).optional(),
  version: z.string().optional(),
});
export type PaymentNoticeIssueRequest = z.infer<typeof PaymentNoticeIssueRequest>;
export const PaymentNoticeIssueResult = z.object({ documentNo: z.string(), issuedAt: z.string(), updatedAt: z.string(), linkUrl: z.string().nullable() });
export type PaymentNoticeIssueResult = z.infer<typeof PaymentNoticeIssueResult>;

/** POST /payment-notices/:id/sent：リンクをコピー・LINE・SMS・印刷（送付状なし）＝送付の記録（確認の期限の起点） */
export const NoticeSent = z.object({ via: z.enum(["copy", "line", "sms", "print"]) });
export type NoticeSent = z.infer<typeof NoticeSent>;

/** 一覧の1行 */
export const PaymentNoticeSummary = z.object({
  id: z.string().uuid(),
  documentNo: z.string().nullable(),
  status: z.enum(["draft", "issued", "voided"]),
  state: NoticeState,
  vendorId: z.string().uuid(),
  vendorName: z.string(),
  group: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  subject: z.string(),
  issueDate: z.string(),
  total: z.number().int(),
  waitingDays: z.number().int().nullable(),
  deadline: z.string().nullable(),
  confirmedAt: z.string().nullable(),
  purchaseId: z.string().uuid().nullable(),
  updatedAt: z.string(),
});
export type PaymentNoticeSummary = z.infer<typeof PaymentNoticeSummary>;
export const PaymentNoticeList = z.object({
  items: z.array(PaymentNoticeSummary),
  counts: z.object({ waiting: z.number().int(), confirmed: z.number().int(), all: z.number().int() }),
  total: z.number().int(),
});
export type PaymentNoticeList = z.infer<typeof PaymentNoticeList>;

/** 確認ページ（GET /api/v1/n/:token）の頭 */
export const PaymentNoticePublicPage = z.object({
  company: z.string(),
  product: z.enum(["genba", "eigyo"]),
  title: z.string(),
  docNo: z.string(),
  subject: z.string(),
  total: z.number().int(),
  issueDate: z.string(),
  /** 確認の期限の日（送付の日＋n日。送付の記録が無ければ null） */
  deadline: z.string().nullable(),
  confirmDays: z.number().int(),
  pages: z.number().int(),
  /** 送った会社でログイン中の人（確かめ用に見るだけ＝押せない） */
  sender: z.boolean(),
  confirmed: z.object({ at: z.string(), recordNo: z.string(), method: z.enum(["screen", "expired"]) }).nullable(),
  disputed: z.object({ at: z.string() }).nullable(),
});
export type PaymentNoticePublicPage = z.infer<typeof PaymentNoticePublicPage>;
export const NoticeConfirmResult = z.object({ at: z.string(), recordNo: z.string() });
export type NoticeConfirmResult = z.infer<typeof NoticeConfirmResult>;
export const NoticeDisputeRequest = z.object({ reason: z.string().trim().min(1, "内容を入れてください").max(PAYMENT_NOTICE_LIMITS.reasonMax) });
export type NoticeDisputeRequest = z.infer<typeof NoticeDisputeRequest>;

/** 状態の文字（一覧と見出しで同じ）。{md} は MM/DD */
export function noticeStateText(s: NoticeState, x: { waitingDays: number | null; confirmedAt: string | null; documentNo: string | null }): string {
  const md = (iso: string | null) => {
    if (!iso) return "";
    const t = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T00:00:00Z`) : new Date(Date.parse(iso) + 9 * 3600_000);
    return `${String(t.getUTCMonth() + 1).padStart(2, "0")}/${String(t.getUTCDate()).padStart(2, "0")}`;
  };
  switch (s) {
    case "draft":
      return x.documentNo ? PAYMENT_NOTICE_MSG.chipUnissued : PAYMENT_NOTICE_MSG.chipDraft;
    case "unissued":
      return PAYMENT_NOTICE_MSG.chipUnissued;
    case "unsent":
      return PAYMENT_NOTICE_MSG.chipUnsent;
    case "waiting":
      return PAYMENT_NOTICE_MSG.chipWaiting.replace("{n}", String(x.waitingDays ?? 0));
    case "confirmed":
      return PAYMENT_NOTICE_MSG.chipConfirmed.replace("{md}", md(x.confirmedAt));
    case "expired":
      return PAYMENT_NOTICE_MSG.chipExpired;
    case "disputed":
      return PAYMENT_NOTICE_MSG.chipDisputed;
  }
}

/** 画面・API の文（{paymentNotice}・{vendor}・{project}・{order} は会社の言葉＝画面は t()、API は fillLabels） */
export const PAYMENT_NOTICE_MSG = {
  title: "{paymentNotice}",
  list: "{paymentNotice}",
  create: "{paymentNotice}作成",
  newTitle: "{paymentNotice}作成",
  notFound: "その{paymentNotice}は見つかりません",
  vendorRequired: "{vendor}を選んでください",
  vendorInvalid: "{paymentNotice}の宛先は{vendor}だけです",
  orderNotAccepted: "承諾済の{order}から{paymentNotice}を作成できます",
  stale: "他の利用者が変更しました。再読込しますか？",
  notDraft: "下書きの{paymentNotice}だけ修正できます",
  noLines: "明細が1行もありません。行を追加してから発行してください",
  companyRequired: "会社名を入れてください",
  issue: "発行",
  issueTitle: "発行前の確認",
  issueConfirm: "発行",
  issued: "{paymentNotice} {no} を発行しました。",
  unissue: "取消",
  unissued: "{paymentNotice} {no} を取り消して下書きに戻しました。",
  unissueConfirmed: "確認済の{paymentNotice}は取り消せません。仕入の画面で修正してください",
  unissueDisabled: "出した帳票の取消しは設定で止めています",
  deleteDraft: "下書き削除",
  pdfDraft: "PDF で確認（透かし入り）",
  stateMenu: "{paymentNotice}の操作",
  colSubject: "件名",
  source: "元",
  issueNote: "相手が確認ページで「確認」を押すか、送付後 {days}日を過ぎて連絡が無ければ確認済になり、仕入が確定します。",
  /** チップ */
  chipDraft: "下書き",
  /** 発行を取消して下書きに戻した回（番号あり。#276＝見積書の #273 と同じ言葉） */
  chipUnissued: UNISSUED_MSG.chip,
  chipUnsent: "未送付",
  chipWaiting: "確認待ち {n}日",
  chipConfirmed: "確認済 {md}",
  chipExpired: "確認済（期限経過）",
  chipDisputed: "誤りあり",
  /** 紙の画面の右の枠 */
  via: "送信方法",
  viaLink: "リンク",
  linkCopy: "リンクコピー",
  linkCopied: "確認ページのリンクをコピーしました。",
  linkCopyFailed: "コピーできませんでした。リンクの欄を選んでコピーしてください",
  linkLine: "LINE で送る",
  linkSms: "SMS で送る",
  linkExpires: "期限 {date}",
  linkNone: "確認ページのリンクがありません",
  linkReissue: "リンク再発行",
  linkReissued: "確認ページのリンクを出し直しました。前のリンクは使えません。",
  confirmPanel: "相手の確認",
  confirmDeadline: "確認の期限",
  confirmDeadlineNone: "送付すると決まります（送付後 {days}日）",
  confirmRecord: "確認の記録",
  confirmMethod: "方法",
  confirmScreen: "確認ページ",
  confirmExpired: "期限経過（連絡なし）",
  confirmAt: "日時",
  confirmBy: "送り先",
  confirmRecordNo: "記録番号",
  disputeRecord: "誤りがあると連絡がありました",
  disputeHint: "内容を確かめ、取消して修正し、もう一度発行してください。",
  purchaseOpen: "仕入表示",
  vendorRegNo: "登録番号",
  vendorUnqualified: "適格請求書発行事業者以外",
  /** 右下の通知 */
  noticeConfirmed: "{name} が {paymentNotice} {no} を確認しました。仕入を確定しました。",
  noticeDisputed: "{name} から {paymentNotice} {no} に誤りがあると連絡がありました。",
  /** {order}の「⋯」・仕入の一覧 */
  orderMenuCreate: "{paymentNotice}作成",
  listCreate: "{paymentNotice}作成",
  listTab: "{paymentNotice}",
  listTabs: "{paymentNotice}を状態で絞る",
  empty: "{paymentNotice}はまだありません",
  searchLabel: "{vendor}・番号・件名で絞り込み",
  colIssueDate: "発行日",
  colNo: "No.",
  colVendor: "{vendor}",
  colProject: "{project}",
  colAmount: "金額（税込）",
  colState: "状態",
  /** 設定 → {order} */
  settingsConfirmDays: "確認の期限",
  settingsConfirmDaysUnit: "日",
  settingsConfirmDaysHelp: "{paymentNotice}の紙に「送付後 n日以内にご連絡ください。ご連絡がない場合は確認いただいたものとします。」と印字します。n日を過ぎて連絡が無ければ確認済になり、仕入が確定します。3〜30日。",
  settingsName: "名称",
  settingsConfirmDaysRange: "3〜30日で入れてください",
  /** メール・紙 */
  mailDisputeSubject: "{paymentNotice}に誤りがあると連絡がありました（{no}）",
  purchaseOrigin: "元 {paymentNotice} {no}",
} as const;

/** 確認ページの文（ログインなし。{company} は発行した会社名） */
export const NOTICE_PUBLIC_MSG = {
  from: "発行：{company}",
  total: "合計（税込）",
  issueDate: "発行日",
  deadline: "確認の期限",
  pdf: "PDF 保存",
  confirm: "確認",
  confirming: "送信中…",
  dispute: "誤りがある ›",
  disputeTitle: "誤りの連絡",
  disputeLabel: "誤りの内容（200字まで）",
  disputeSend: "送信",
  disputeClose: "閉じる",
  disputeDone: "{company}に誤りがあると連絡しました。内容を確かめて連絡があります。",
  disputedChip: "誤りあり",
  disputedNote: "誤りがあると連絡済みです（{at}）。{company}からの連絡をお待ちください。",
  confirmedChip: "確認済 {md}",
  confirmedAt: "確認しました（{at}）",
  confirmedExpired: "期限を過ぎたため確認済になりました（{at}）",
  recordNo: "記録番号 {no}",
  note: "内容に誤りが無ければ「確認」を押してください。誤りがあるときは「誤りがある」から連絡してください。",
  sender: "送った会社の画面です。確認は相手の方が行います。",
  already: "この{paymentNotice}はもう確認済みです",
  answered: "この{paymentNotice}はもう回答済みです",
  gone: "この{paymentNotice}の確認ページは終了しました",
  notFound: "ページが見つかりません",
  tooMany: "しばらくしてから、もう一度お試しください",
} as const;
